import { Box, Button, Stack, Typography } from '@mui/material';
import { Component, ReactNode } from 'react';
import { getCookieCustom } from '../../utils/cookies';
type AppErrorBoundaryProps = {
 children: ReactNode;
};

type AppErrorBoundaryState = {
 error: Error | null;
};

const getErrorFromUnknown = (value: unknown): Error => {
 if (value instanceof Error) {
  return value;
 }

 if (typeof value === 'string') {
  return new Error(value);
 }

 return new Error('Unexpected application error');
};

class AppErrorBoundary extends Component<
 AppErrorBoundaryProps,
 AppErrorBoundaryState
> {
 state: AppErrorBoundaryState = {
  error: null,
 };

 handleWindowError = (event: ErrorEvent) => {
  if (event.error) {
   this.setState({ error: getErrorFromUnknown(event.error) });

   return;
  }

  this.setState({
   error: new Error(event.message || 'Unexpected application error'),
  });
 };

 handleUnhandledRejection = (event: PromiseRejectionEvent) => {
  this.setState({ error: getErrorFromUnknown(event.reason) });
 };

 static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
  return {
   error,
  };
 }

 componentDidCatch(error: Error): void {
  console.error(error);
 }

 componentDidMount(): void {
  window.addEventListener('error', this.handleWindowError);
  window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
 }

 componentWillUnmount(): void {
  window.removeEventListener('error', this.handleWindowError);
  window.removeEventListener(
   'unhandledrejection',
   this.handleUnhandledRejection,
  );
 }

 handleReload = () => {
  window.location.reload();
 };

 render(): ReactNode {
  const { error } = this.state;

  const errorMessage =
   `URL: ${window.location.href}\n\nDate: ${new Date().toISOString()}\n\nUser: ${getCookieCustom('user')}\n\nError: ${error?.stack}` ||
   'Unexpected application error';

  if (error) {
   return (
    <Box
     sx={{
      minHeight: 'calc(100vh - 64px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: 3,
      py: 4,
      bgcolor: 'background.default',
     }}
    >
     <Stack spacing={2} sx={{ maxWidth: 720, width: '100%' }}>
      <Typography variant="h4">Kažkas nutiko</Typography>
      <Typography variant="body1" color="text.secondary">
       {error.message || 'Unexpected application error'}
      </Typography>
      <Box
       component="pre"
       sx={{
        m: 0,
        p: 2,
        minHeight: 300,
        overflow: 'auto',
        borderRadius: 1,
        bgcolor: 'grey.100',
        color: 'grey.900',
        fontSize: 12,
        whiteSpace: 'pre-wrap',
       }}
      >
       {errorMessage}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
       <Button
        variant="outlined"
        color="secondary"
        onClick={() => navigator.clipboard.writeText(errorMessage)}
       >
        Kopijuoti klaidos pranešimą
       </Button>
       <Button variant="contained" color="primary" onClick={this.handleReload}>
        Perkrauti
       </Button>
      </Box>
     </Stack>
    </Box>
   );
  }

  return this.props.children;
 }
}

export default AppErrorBoundary;
